/**
 * Frontage Density Constants - PSP/CRE Tool v3.0
 *
 * Models the relationship between troop density (troops per km of frontage)
 * and casualty rates. High-density formations are more vulnerable to indirect
 * fire, which dominates modern warfare (58% of casualties, Ukraine 2022-2024).
 *
 * @version 1.0.0
 * @date March 10, 2026
 * @category Cat-22 Research Inventory Audit — Force Density / Frontage
 *
 * VERSION 1.0.0 (March 10, 2026) — ENH-022: Initial implementation.
 *   Category 22 of §20 Research Inventory Audit: was "UI only, no calc modifier".
 *   Thresholds derived from:
 *     - Dupuy Institute historical data (TNDM methodology)
 *     - Ukraine 2022-2024 ISW / JHU APL combat analysis:
 *       Russian assault density: 800–1,200 troops/km → 2–4% daily loss
 *       Ukrainian economy-of-force: 50–150 troops/km → 0.5–1% daily loss
 *       Ratio: 3–4× difference attributable primarily to density
 *     - NATO AJMedP-1 §4.2: "Concentrated forces on narrow frontages experience
 *       30–50% higher casualty rates due to increased target density"
 *     - ATP 4-02.2 §2-8: indirect fire accounts for 58% of casualties
 *
 * USAGE:
 *   import { getDensityModifier } from '../constants/frontageDensity';
 *   const result = getDensityModifier(brigade.strength, brigade.position?.frontage_width_km);
 *   // result.modifier — multiply against rawCasualties
 *   // result.band     — 'VERY_DISPERSED' | 'DISPERSED' | 'NORMAL' | etc.
 *   // result.troops_per_km — computed density
 *
 * EXCLUSION RULE:
 *   Units with frontage_width_km = 0 (has_frontage = false, set by Tab2 for support units,
 *   logistics brigades, medical brigades, MARCHING posture) return modifier = 1.0.
 *   Their casualty rate is already reduced by operational_state (SUPPORT_ACTIVE = 0.2x,
 *   TRUE_RESERVE = 0.08x) — density does not apply to units not occupying a frontage.
 *
 * DOCTRINAL NOTE:
 *   NATO standard defensive frontage per brigade: 15–25 km.
 *   Attack frontages (Schwerpunkt): 3–8 km.
 *   Economy-of-force: may exceed 40 km.
 *   "Normal" band calibrated to 200–300 troops/km ≈ a 4,500-strength brigade on 15–22 km.
 */

// ============================================================================
// FRONTAGE DENSITY BANDS
// ============================================================================

/**
 * Five-band density model.
 * max_troops_per_km: upper boundary of the band (exclusive).
 * modifier: multiplier applied to raw battle casualties.
 * label: display string for UI and reports.
 * description: tooltip / report explanation.
 * warning: null = no warning; string = display in reports.
 *
 * Sources: Dupuy TNDM, Ukraine 2022-2024, AJMedP-1 §4.2, ATP 4-02.2 §2-8.
 */
export const FRONTAGE_DENSITY_THRESHOLDS = {
  VERY_DISPERSED: {
    max_troops_per_km: 100,
    modifier: 0.70,
    label: 'Very Dispersed',
    description: '< 100 troops/km — extremely thin line; poor artillery target; high infiltration risk',
    warning: null
  },
  DISPERSED: {
    max_troops_per_km: 200,
    modifier: 0.85,
    label: 'Dispersed',
    description: '100–200 troops/km — economy of force; below standard frontage density',
    warning: null
  },
  NORMAL: {
    max_troops_per_km: 300,
    modifier: 1.00,
    label: 'Normal Density',
    description: '200–300 troops/km — standard NATO defensive density; baseline casualty rate',
    warning: null
  },
  CONCENTRATED: {
    max_troops_per_km: 500,
    modifier: 1.20,
    label: 'Concentrated',
    description: '300–500 troops/km — assault / Schwerpunkt density; +20% artillery vulnerability',
    warning: 'Concentrated formation — increased indirect fire vulnerability (+20% casualties)'
  },
  VERY_CONCENTRATED: {
    max_troops_per_km: Infinity,
    modifier: 1.40,
    label: 'Very Concentrated',
    description: '> 500 troops/km — mass assault density; +40% artillery vulnerability',
    warning: 'Very concentrated — extreme indirect fire vulnerability (+40% casualties)'
  }
};

/**
 * Modifier returned when no frontage is applicable.
 * Covers: support units (has_frontage = false), MARCHING posture,
 * logistics/medical brigades. Density concept does not apply.
 */
export const DENSITY_NO_FRONTAGE_MODIFIER = 1.0;

// ============================================================================
// HELPER: getDensityModifier
// ============================================================================

/**
 * Calculate the density modifier for a unit given its strength and frontage.
 *
 * @param {number} troops        - Unit effective strength
 * @param {number} frontageKm    - Frontage width in km (0 = not on frontage)
 * @returns {{
 *   modifier: number,          - Multiplier for rawCasualties (0.70–1.40, or 1.0 if no frontage)
 *   band: string,              - Band key or 'NO_FRONTAGE'
 *   troops_per_km: number,     - Computed density (0 if no frontage)
 *   label: string,             - Display label
 *   description: string,       - Explanation string
 *   warning: string|null       - Warning text, or null
 *   applicable: boolean        - false when frontage = 0 (density not relevant)
 * }}
 */
export function getDensityModifier(troops, frontageKm) {
  // Guard: no frontage → density not applicable, neutral modifier
  if (!frontageKm || frontageKm <= 0 || !troops || troops <= 0) {
    return {
      modifier: DENSITY_NO_FRONTAGE_MODIFIER,
      band: 'NO_FRONTAGE',
      troops_per_km: 0,
      label: 'No Frontage',
      description: 'Unit is not occupying a frontage — density modifier not applicable',
      warning: null,
      applicable: false
    };
  }

  const density = troops / frontageKm;

  // Walk bands in order (ascending threshold)
  for (const [key, band] of Object.entries(FRONTAGE_DENSITY_THRESHOLDS)) {
    if (density < band.max_troops_per_km) {
      return {
        modifier: band.modifier,
        band: key,
        troops_per_km: Math.round(density),
        label: band.label,
        description: band.description,
        warning: band.warning,
        applicable: true
      };
    }
  }

  // Fallback (unreachable — VERY_CONCENTRATED has max = Infinity)
  const last = FRONTAGE_DENSITY_THRESHOLDS.VERY_CONCENTRATED;
  return {
    modifier: last.modifier,
    band: 'VERY_CONCENTRATED',
    troops_per_km: Math.round(density),
    label: last.label,
    description: last.description,
    warning: last.warning,
    applicable: true
  };
}

// ============================================================================
// DOCTRINAL REFERENCE — NATO STANDARD FRONTAGES (informational, not used in calc)
// ============================================================================

/**
 * Standard NATO frontage norms (AJP-3.2 / FM 3-90).
 * Displayed in Tab2 as guidance; not a hard constraint.
 * Planner may exceed these for economy-of-force missions.
 */
export const NATO_STANDARD_FRONTAGES = {
  BATTALION: {
    defense_km: { min: 3,  max: 6,  nominal: 4  },
    attack_km:  { min: 1,  max: 3,  nominal: 2  },
    label: 'Battalion'
  },
  BRIGADE: {
    defense_km: { min: 15, max: 25, nominal: 20 },
    attack_km:  { min: 3,  max: 8,  nominal: 5  },
    label: 'Brigade'
  },
  DIVISION: {
    defense_km: { min: 40, max: 70, nominal: 55 },
    attack_km:  { min: 10, max: 25, nominal: 15 },
    label: 'Division'
  }
};


// ============================================================================
// DEFAULT FRONTAGE BY ECHELON (2026-10-13, Tab 2 plan step 6)
// ============================================================================
// Tab 2 used 90 km as the default total frontage, a corps number: a one-brigade scenario got 55 troops per km and the
// 'very dispersed' band (x0.70 casualties) by default. The default is now per combat brigade.
// EDUCATED_GUESS between two sources: US Army course material puts a defending brigade sector at about 10 to 16 km wide; the note above
// (NATO standard defensive frontage per brigade 15 to 25 km) calibrates the 'normal' band on 15 to 22 km. 18 km gives
// 280 troops per km for a 5,000 brigade (normal band). Choose the real frontage in Tab 2.
export const DEFAULT_FRONTAGE_PER_BRIGADE_KM = 18;
export const DEFAULT_FRONTAGE_STATUS = { status: 'EDUCATED_GUESS', basis: 'Between 10 to 16 km (US course material) and 15 to 25 km (NATO defensive frontage per brigade).' };

/** Default total frontage (km) for a number of frontage-holding brigades; at least one. */
export function defaultTotalFrontageKm(combatBrigades) {
  if (!Number.isFinite(combatBrigades) || combatBrigades < 0) {
    throw new Error(`frontageDensity.defaultTotalFrontageKm: the number of combat brigades must be zero or more (got ${combatBrigades})`);
  }
  return DEFAULT_FRONTAGE_PER_BRIGADE_KM * Math.max(1, combatBrigades);
}
